import {
  COLORS,
  MeasureType,
  PlatformTypeEnum,
  ROUTE_LOADS,
} from '@/common/constants';
import { ContractStatusesEnum } from '@/common/constants/contract.statuses';
import { ISidebarItem } from '@/common/models/ISidebar';
import useStore from '@/lib/store';
import { cn } from '@/lib/utils';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useParams, useSearchParams } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { ChildView } from './child-view';

interface IProps {
  item: ISidebarItem & {
    children?: ISidebarItem[];
  };
}

export const SidebarItem: FC<IProps> = ({ item }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { id, status } = useParams<{ status: string; id: string }>();
  const contentRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState('0px');
  const [isOpen, setIsOpen] = useState(false);
  const { user } = useStore();
  const isCotton = user?.platform_type === PlatformTypeEnum.COTTON;

  const isContractDeleted =
    item?.farmer_contract_status === ContractStatusesEnum.DELETED ||
    item?.farmer_contract_status === ContractStatusesEnum.CANCELED;

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setHeight('0px');
    }
  }, [isOpen]);

  const handleClick = () => {
    if (item.current_status === MeasureType.COMPLETE) {
      setIsOpen((prev) => !prev);
    }
  };

  return (
    <Fragment>
      <NavLink
        to={{
          pathname: `${ROUTE_LOADS}/${status || 'ALL'}/${item.id}`,
          search: `?${searchParams.toString()}`,
        }}
        onClick={handleClick}
        className={cn(
          'py-3 px-6 border-b bg-white cursor-pointer  grid grid-cols-12 gap-[4px]  transition-all relative   duration-300',
          id === item?.id && 'bg-green-default/10',
        )}
      >
        {isContractDeleted && (
          <div className="absolute inset-0 flex items-center justify-center z-10 bg-[#f5f5f5] opacity-75">
            <Badge
              className={cn('text-[12px] py-[1px] text-white', 'bg-gray-500')}
            >
              {t('deleted.contract')}
            </Badge>
          </div>
        )}
        <span
          className={cn(
            id === item?.id ? 'bg-green-default  opacity-100' : 'opacity-0',
            'duration-300 absolute left-0 inset-y-0 h-full w-1',
          )}
        />
        <Badge
          className={cn(
            'text-[11px] py-[1px] text-white absolute top-1.5 right-2',
            COLORS?.find((c) => c.has_analysis === item?.has_analysis)?.color,
          )}
        >
          {t(
            COLORS?.find((c) => c?.has_analysis === item?.has_analysis)?.name ||
              '',
          )}
        </Badge>

        <span
          className={cn(
            id === item?.id ? 'bg-green-default  opacity-100' : 'opacity-0',
            'duration-300 absolute left-0 inset-y-0 h-full w-1',
          )}
        />
        <div className="flex  items-center gap-[18px] col-span-10">
          <div className="flex flex-col items-start">
            <p className="text-[11px] font-medium text-gray-graphite">
              {t('organization.stir')}
            </p>
            <p className="text-sm text-midnight-default">{item?.tax_number}</p>
          </div>
          <div className="flex flex-col items-start">
            <p className="text-[11px] font-medium text-gray-graphite">
              {t('organization.name')}
            </p>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-sm text-dark-blue w-[calc(400px-130px)] font-semibold line-clamp-1">
                  {item?.farmer_name}
                </p>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-medium">{item?.farmer_name}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div className="flex flex-col items-start col-span-3">
          <p className="text-[11px] font-medium text-gray-graphite">
            {t('id.number')}
          </p>
          <p className="text-sm text-midnight-default">{item?.number}</p>
        </div>
        <div className="flex flex-col items-start col-span-4">
          <p className="text-[11px] font-medium text-gray-graphite">
            {t('contract.number')}
          </p>
          <p className="text-sm text-midnight-default">
            {isCotton ? item?.contract_number : item?.farmer_contract_number}
          </p>
        </div>

        <div className="flex flex-col items-start col-span-4">
          <p className="text-[11px] font-medium text-gray-graphite">
            {t('contract.type')}
          </p>
          <p className="text-sm text-midnight-default">
            {item?.deal_type === 'FUTURES'
              ? t('FUTURES')
              : item?.deal_type === 'FORWARD'
                ? t('FORWARD')
                : t('SPOT')}
          </p>
        </div>
      </NavLink>
      {!isContractDeleted &&
        item?.current_status === MeasureType.COMPLETE &&
        item?.is_separated && (
          <div
            ref={contentRef}
            style={{ maxHeight: height }}
            className="overflow-hidden transition-[max-height] duration-300 ease-in-out bg-gray-50 text-sm text-gray-700 border-b"
          >
            {item?.children?.map((child) => (
              <ChildView key={child?.id} item={child} />
            ))}
          </div>
        )}
    </Fragment>
  );
};
